import { useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AppLayout } from '@/components/layout/app-layout';
import { PageHeader } from '@/components/patterns';
import { useAiIntelligenceStore } from '../store/ai-intelligence-store';
import { SimulatorTab } from '../components/simulator-tab';
import { PredictionsTab } from '../components/predictions-tab';
import { RecommendationsTab } from '../components/recommendations-tab';
import { InsightsTab } from '../components/insights-tab';
import { Brain, Play, Shield, Lightbulb, Sparkles, Info } from 'lucide-react';
import type { AiIntelligenceTab } from '../types/ai-intelligence.types';

const TAB_DESCRIPTIONS: Record<AiIntelligenceTab, string> = {
  simulator:
    'Prueba escenarios hipotéticos sobre cotizaciones reales: aplica descuentos, cambia cantidades o precios y ve como afecta el margen, el precio final y si necesitaría aprobación.',
  predictions:
    'Evalua el riesgo de una cotización antes de enviarla a aprobación, y analiza que tan efectivo es un modelo de pricing basandote en datos historicos reales.',
  recommendations:
    'Obtiene sugerencias de precio basadas en cotizaciones aprobadas anteriormente, y genera justificaciones estructuradas para cuando necesitas pedir una excepción de precio.',
  insights:
    'Dashboard inteligente que genera narrativas automaticas, detecta anomalias, compara métricas vs el periodo anterior y muestra rankings de clientes y vendedores.',
};

export function AiIntelligencePage() {
  const { activeTab, setActiveTab, fetchSelectors } = useAiIntelligenceStore();

  useEffect(() => {
    fetchSelectors();
  }, [fetchSelectors]);

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        {/* El encabezado de Cotiza (artefacto, 19-oct). */}
        <div data-tour="ai-header">
          <PageHeader
            modulo="cotiza"
            etiqueta="Cotiza"
            icono={Brain}
            titulo="Inteligencia de Pricing"
            subtitulo="Herramientas de análisis con IA para afinar tu estrategia de precios."
          />

          {/* Descripcion general */}
          <div className="mt-3 p-4 rounded-lg border border-primary/20 bg-primary/5">
            <div className="flex gap-2">
              <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div className="text-sm text-muted-foreground">
                <p>
                  Esta seccion analiza tus{' '}
                  <strong className="text-foreground">
                    cotizaciones y modelos de pricing reales
                  </strong>{' '}
                  para darte insights accionables. Todas las herramientas trabajan con los datos de
                  tu empresa — no son datos de ejemplo.
                </p>
                <p className="mt-1">
                  Usa las <strong className="text-foreground">4 pestañas</strong> de abajo para
                  navegar entre las distintas herramientas.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as AiIntelligenceTab)}>
          <TabsList data-tour="ai-tabs" className="grid w-full grid-cols-4">
            <TabsTrigger value="simulator" className="gap-2">
              <Play className="w-4 h-4" />
              <span className="hidden sm:inline">Simulador</span>
            </TabsTrigger>
            <TabsTrigger value="predictions" className="gap-2">
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Predicciones</span>
            </TabsTrigger>
            <TabsTrigger value="recommendations" className="gap-2">
              <Lightbulb className="w-4 h-4" />
              <span className="hidden sm:inline">Recomendaciones</span>
            </TabsTrigger>
            <TabsTrigger value="insights" className="gap-2">
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">Insights</span>
            </TabsTrigger>
          </TabsList>

          {/* Descripcion dinamica del tab activo */}
          <p className="text-sm text-muted-foreground mt-3 mb-1 px-1">
            {TAB_DESCRIPTIONS[activeTab]}
          </p>

          <TabsContent value="simulator" data-tour="ai-simulator">
            <SimulatorTab />
          </TabsContent>
          <TabsContent value="predictions" data-tour="ai-predictions">
            <PredictionsTab />
          </TabsContent>
          <TabsContent value="recommendations">
            <RecommendationsTab />
          </TabsContent>
          <TabsContent value="insights" data-tour="ai-insights">
            <InsightsTab />
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
